import { useEffect } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Trash2, Plus } from "lucide-react";
import { toast } from "@/components/ui/notify";
import { FinishProductionRequestSchema, type FinishProductionRequest } from "@opus-dt/shared-types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ModalOverlay } from "@/components/ui/modal-overlay";
import { useLineTrackedByMachine } from "@/hooks/useLineTrackedByMachine";
import { useLineMachines } from "@/features/lines/hooks";
import { useFinishRun } from "./hooks";
import { getFriendlyErrorMessage } from "@/lib/api-client";
import { formatQuantity } from "@/lib/quantity-format";

interface Props {
  lineId: string;
  runId: string;
  open: boolean;
  onClose: () => void;
  // Produzido atual da run (rollup); usado no modo por-máquina (read-only).
  producedQty?: number;
}

export function FinishProductionDialog({ lineId, runId, open, onClose, producedQty }: Props) {
  const finish = useFinishRun(lineId);
  // No modo por-máquina o produzido vem do rollup (read-only); o input some.
  const trackedByMachine = useLineTrackedByMachine(lineId);
  // Enquanto as máquinas carregam, trackedByMachine é falso por default; não mostre
  // o input editável até saber o modo (evita pedir um número que o backend descarta no rollup).
  const { isLoading: machinesLoading } = useLineMachines(lineId, 30_000);
  const rollupQty = producedQty ?? 0;
  const form = useForm<FinishProductionRequest>({
    resolver: zodResolver(FinishProductionRequestSchema),
    defaultValues: { observations: "", losses: [] },
  });
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "losses",
  });

  // Mantém producedQty válido (schema exige) sem expor o input; o backend ignora/recompute.
  useEffect(() => {
    if (trackedByMachine) form.setValue("producedQty", rollupQty);
  }, [trackedByMachine, rollupQty, form]);

  if (!open) return null;

  async function onSubmit(values: FinishProductionRequest) {
    try {
      await finish.mutateAsync({ runId, data: values });
      toast.success("Produção finalizada.");
      onClose();
      form.reset();
    } catch (err) {
      const msg = getFriendlyErrorMessage(err);
      toast.error("Falha ao finalizar.", { description: msg });
    }
  }

  return (
    <ModalOverlay
      onClose={onClose}
      title="Finalizar produção"
      className="max-w-xl overflow-y-auto"
      confirmDismiss={form.formState.isDirty}
    >
      <CardHeader>
        <CardTitle id="finish-production-title">Finalizar produção</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={(e) => void form.handleSubmit(onSubmit)(e)} className="space-y-4">
          {trackedByMachine ? (
            <div className="space-y-2">
              <Label>Produzido (por máquina)</Label>
              <p className="rounded-md border bg-muted/30 px-3 py-2 font-mono text-sm">
                {formatQuantity(rollupQty)}
              </p>
              <p className="text-xs text-muted-foreground">
                Esta linha registra produção por máquina. O total vem da soma dos reportes.
              </p>
            </div>
          ) : machinesLoading ? (
            <div className="space-y-2">
              <Label>Quantidade produzida</Label>
              <p className="text-xs text-muted-foreground">Carregando modo da linha…</p>
            </div>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="producedQty">Quantidade produzida</Label>
              <Input
                id="producedQty"
                type="number"
                min={0}
                // Spec 081 — quantidade aceita fração: sem `step`, o input recusa "415,5".
                step="any"
                placeholder="0"
                {...form.register("producedQty", { valueAsNumber: true })}
              />
              {form.formState.errors.producedQty ? (
                <p className="text-xs text-destructive" role="alert">
                  {form.formState.errors.producedQty.message ?? "Informe a quantidade produzida."}
                </p>
              ) : null}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="observations">Observações (opcional)</Label>
            <Input id="observations" {...form.register("observations")} />
          </div>

          {trackedByMachine ? (
            // No modo por-máquina o refugo já foi reportado por máquina;
            // o finish usa o acumulado, sem pedir um refugo final à parte.
            <p className="rounded-md border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
              O refugo desta linha é registrado por máquina; o total já está contabilizado.
            </p>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Perdas</Label>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => append({ lossType: "REJECT", quantity: 1 })}
                >
                  <Plus className="h-3 w-3" />
                  Adicionar perda
                </Button>
              </div>
              <div className="space-y-2">
                {fields.length === 0 ? (
                  <p className="text-xs text-muted-foreground">Nenhuma perda registrada.</p>
                ) : (
                  fields.map((field, idx) => (
                    <div
                      key={field.id}
                      className="flex items-end gap-2 rounded-md border bg-card p-2"
                    >
                      <div className="flex-1 space-y-1">
                        <Label htmlFor={`loss-${idx}-type`} className="text-xs">
                          Tipo
                        </Label>
                        <Select
                          id={`loss-${idx}-type`}
                          {...form.register(`losses.${idx}.lossType`)}
                        >
                          <option value="REJECT">Refugo</option>
                          <option value="WASTE">Desperdício</option>
                          <option value="REWORK">Retrabalho</option>
                        </Select>
                      </div>
                      <div className="w-24 space-y-1">
                        <Label htmlFor={`loss-${idx}-qty`} className="text-xs">
                          Qtd
                        </Label>
                        <Input
                          id={`loss-${idx}-qty`}
                          type="number"
                          min={0}
                          step="any"
                          {...form.register(`losses.${idx}.quantity`, { valueAsNumber: true })}
                        />
                      </div>
                      <div className="flex-1 space-y-1">
                        <Label htmlFor={`loss-${idx}-reason`} className="text-xs">
                          Motivo (opcional)
                        </Label>
                        <Input
                          id={`loss-${idx}-reason`}
                          {...form.register(`losses.${idx}.reason`)}
                        />
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label="Remover perda"
                        onClick={() => remove(idx)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={finish.isPending || machinesLoading}>
              {finish.isPending ? "Finalizando…" : "Finalizar"}
            </Button>
          </div>
        </form>
      </CardContent>
    </ModalOverlay>
  );
}
